/**
 * DTO SLA-C1-frozen (revisione 2026-09-16, estensione escalation 2026-09-18).
 *
 * Fonti: `plans/SLA_Client_Plan.md` sez. 4 (F1), sez. 5 (contratti C1) e
 * sez. 11 (variazione escalation); `Flowenti-Server/plans/SLA_Server_Plan.md`
 * sez. 10-11 per le rotte realmente implementate.
 *
 * Convenzione delle chiavi: il JSON del backend Delphi non e' uniforme e il
 * DTO lo rispecchia senza inventare nomi.
 * - Risposte SQL (instances, kpi, diagram) e payload in ingresso (PUT/PATCH/POST):
 *   snake_case, come i nomi dei campi del DataSet e le chiavi lette dai controller.
 * - Risposte dei modelli DMVC serializzati (`sla_definition`, `sla_settings`,
 *   `sla_user_preferences`): solo minuscolo, senza underscore, perche' le classi
 *   Server usano `[MVCNameCase(ncLowerCase)]` applicato ai nomi delle property
 *   (`TargetType` -> `targettype`). Vedi `slaDefinitionDtoToInput` in `slaApi.ts`.
 *
 * Nessun calcolo del calendario e' fatto qui: secondi residui, scadenze e
 * `clock_running` arrivano dal Server (piano Client sez. 1).
 */

// ─── Costanti del contratto (Server: SlaDefinition.pas / SlaInstance.pas) ─────

/** target_type */
export const SLA_TARGET_PROCESS = 1;
export const SLA_TARGET_ACTIVITY = 2;
export type SlaTargetType = typeof SLA_TARGET_PROCESS | typeof SLA_TARGET_ACTIVITY;

/** duration_format */
export const SLA_FORMAT_RELATIVE = 1;
export const SLA_FORMAT_ABSOLUTE = 2;
export type SlaDurationFormat = typeof SLA_FORMAT_RELATIVE | typeof SLA_FORMAT_ABSOLUTE;

/** duration_unit */
export const SLA_UNIT_MINUTES = 1;
export const SLA_UNIT_HOURS = 2;
export const SLA_UNIT_DAYS = 3;
export const SLA_UNIT_WEEKS = 4;
export type SlaDurationUnit =
  | typeof SLA_UNIT_MINUTES
  | typeof SLA_UNIT_HOURS
  | typeof SLA_UNIT_DAYS
  | typeof SLA_UNIT_WEEKS;

/** health: nessun sesto stato, un breach escalato resta un breach (sez. 11.1) */
export const SLA_HEALTH_ON_TIME = 0;
export const SLA_HEALTH_AT_RISK = 1;
export const SLA_HEALTH_BREACHED = 2;
export type SlaHealth =
  | typeof SLA_HEALTH_ON_TIME
  | typeof SLA_HEALTH_AT_RISK
  | typeof SLA_HEALTH_BREACHED;

/** Livelli di escalation ammessi dal CHECK della migrazione 20260918 (1..5) */
export const SLA_ESCALATION_MIN_LEVELS = 1;
export const SLA_ESCALATION_MAX_LEVELS = 5;

/**
 * Paginazione 50 di `GET /instances` (piano Server sez. 5). Il Server costruisce
 * l'offset come `page * pagesize`, quindi `page` e' 0-based; i valori del meta
 * sono stringhe (`TMVCStringDictionary`).
 */
export const SLA_INSTANCES_PAGE_SIZE = 50;

export interface SlaPaginationMeta {
  curr_page: string;
  prev_page_uri?: string;
  next_page_uri?: string;
}

// ─── Definizioni modello/versione (GET/PUT /sla/organizations/{org}/models/{m}/definitions)

/** Elemento di `definitions[]` come serializzato dal modello DMVC (minuscolo). */
export interface SlaDefinitionDto {
  id?: number;
  idorganization: number;
  idprocessmodel: number;
  modelrevision: number;
  targettype: SlaTargetType;
  targetdesignerid: string | null;
  durationformat: SlaDurationFormat;
  durationvalue: number | null;
  durationunit: SlaDurationUnit | null;
  riskremainingpercent: number | null;
  responsibleuserid: number | null;
  responsibleroleid: number | null;
  /** NULL = nessuna escalation configurata per questo target (sez. 11.1) */
  escalationafterworkinghours: number | null;
  escalationlevels: number;
  /** NULL = si ricade sul responsabile configurato (sez. 11.1) */
  escalationroleid: number | null;
  active: boolean;
  updatedat: string | null;
  updatedby: number | null;
}

export interface SlaDefinitionsResponse {
  definitions: SlaDefinitionDto[];
  /** Serializzato come stringa dal controller: non assumerne il tipo numerico. */
  meta: { model_revision: string };
}

/** Elemento di `definitions[]` nel PUT: chiavi snake_case lette dal controller. */
export interface SlaDefinitionInput {
  target_type: SlaTargetType;
  /**
   * `false` = rimuove (disattiva) lo SLA del target; assente = upsert. Gli
   * elementi non inviati restano invariati.
   */
  active?: boolean;
  target_designerid?: string | null;
  duration_format: SlaDurationFormat;
  duration_value?: number | null;
  duration_unit?: SlaDurationUnit | null;
  risk_remaining_percent?: number | null;
  responsible_user_id?: number | null;
  responsible_role_id?: number | null;
  escalation_after_working_hours?: number | null;
  escalation_levels?: number | null;
  escalation_role_id?: number | null;
}

export interface SlaDefinitionsPutRequest {
  /** Revisione su cui si sta scrivendo (deve essere > 0). */
  model_revision: number;
  /** Revisione attesa: se diverge da quella corrente il Server risponde 409. */
  expected_revision: number;
  definitions: SlaDefinitionInput[];
}

/** 202: la validazione e' SOFT, gli avvisi non bloccano il salvataggio. */
export interface SlaDefinitionsPutResponse {
  meta: { warnings: string[] };
}

// ─── Escalation (variazione 2026-09-18, piano Client sez. 11.1) ──────────────

/**
 * Vista design-time dell'escalation di un target. Le colonne sono **identiche**
 * su `sla_definition` e `sla_policy_target` (sez. 11.1), ma il Client vede solo
 * la definizione: la policy e' lo snapshot che il runtime congela all'avvio.
 *
 * Qui non c'e' mai lo stato raggiunto: il contratto non espone nel payload
 * istanza il livello di escalation in corso (sez. 11.5 punto 2), quindi questa
 * struttura descrive cosa e' configurato, non cosa e' gia' successo.
 */
export interface SlaEscalationConfig {
  /** true solo con ritardo valorizzato e > 0: e' la regola `HasEscalation` del Server. */
  configured: boolean;
  /** Ore lavorative fra un livello e il successivo; NULL = nessuna escalation. */
  afterWorkingHours: number | null;
  /** 1..5 per il CHECK della migrazione; 1 e' il DEFAULT della colonna. */
  levels: number;
  /** NULL = ricaduta sul responsabile configurato, non "nessun destinatario". */
  roleId: number | null;
}

/** `escalation_levels` e' `smallint NOT NULL DEFAULT 1` con CHECK 1..5 (sez. 11.1). */
export const isSlaEscalationLevel = (levels: number): boolean =>
  Number.isInteger(levels) &&
  levels >= SLA_ESCALATION_MIN_LEVELS &&
  levels <= SLA_ESCALATION_MAX_LEVELS;

/**
 * Regola `HasEscalation` del Server: il ritardo configura l'escalation solo se
 * valorizzato e `> 0`; il CHECK della migrazione 20260918 impone `> 0` quando
 * `escalation_after_working_hours` non e' NULL (sez. 11.1).
 *
 * Predicato unico della regola: i consumatori che leggono una `SlaDefinitionDto`
 * (Monitor, editor) non la riscrivono con esiti diversi.
 */
export const isSlaEscalationDelayConfigured = (
  afterWorkingHours: number | null,
): boolean => afterWorkingHours !== null && afterWorkingHours > 0;

/**
 * Configurazione di escalation di una definizione. Non deduce nulla dal toggle
 * di organizzazione `escalationenabled`: sono due gate distinti (sez. 11.1) e
 * chi vuole mostrare "escalation attiva" deve avere anche le settings.
 */
export const slaEscalationFromDefinition = (
  dto: SlaDefinitionDto,
): SlaEscalationConfig => ({
  configured: isSlaEscalationDelayConfigured(dto.escalationafterworkinghours),
  afterWorkingHours: dto.escalationafterworkinghours,
  levels: dto.escalationlevels,
  roleId: dto.escalationroleid,
});

/**
 * Primo gate del doppio gate di runtime (`Job.SLA.Worker.pas`): l'escalation
 * per target resta **inerte** se l'organizzazione non ha `enabled` e
 * `escalation_enabled` entrambi attivi (sez. 11.1).
 */
export const isSlaEscalationRuntimeEnabled = (
  settings: Pick<SlaSettingsDto, "enabled" | "escalationenabled">,
): boolean => settings.enabled && settings.escalationenabled;

/**
 * true quando la configurazione design-time del target produrrebbe davvero
 * un'escalation. Non dice nulla sulle istanze **gia' avviate**: la policy e'
 * congelata all'avvio e le modifiche a design-time non le escalationo (sez. 11.1).
 */
export const isSlaEscalationActive = (
  dto: SlaDefinitionDto,
  settings: Pick<SlaSettingsDto, "enabled" | "escalationenabled">,
): boolean =>
  slaEscalationFromDefinition(dto).configured &&
  isSlaEscalationRuntimeEnabled(settings);

// ─── Preview (POST /sla/organizations/{org}/preview) ─────────────────────────

export interface SlaPreviewRequest {
  /** Riferimento temporale esplicito: rende la preview riproducibile. */
  reference_at?: string | null;
  definition: {
    target_type: SlaTargetType;
    duration_format: SlaDurationFormat;
    duration_value?: number | null;
    duration_unit?: SlaDurationUnit | null;
    risk_remaining_percent?: number | null;
  };
}

export interface SlaPreviewResponse {
  /** false = stima non disponibile (loop/attese non stimabili), non un errore. */
  estimable: boolean;
  as_of: string;
  /** Stringa vuota quando non calcolabile. */
  deadline_at: string;
  risk_at: string;
  warnings: string[];
}

// ─── Istanze aperte (GET /sla/organizations/{org}/instances) ─────────────────

export interface SlaInstance {
  id: number;
  idprocess: number;
  target_type: SlaTargetType;
  target_designerid: string | null;
  revision: number;
  health: SlaHealth;
  overridden: boolean;
  timezone_id: string;
  started_at: string | null;
  original_deadline_at: string | null;
  /** Deadline vigente: le revisioni non cancellano la promessa originale. */
  effective_deadline_at: string | null;
  risk_at: string | null;
  activity_description: string;
  process_description: string;
  /** 0 = task non ancora preso in carico. */
  iduser: number;
  idswimlane: number;
  /** Numeri, non stringhe tradotte; null quando il clock non e' calcolabile. */
  remaining_working_seconds: number | null;
  overdue_working_seconds: number | null;
  clock_running: boolean | null;
  next_clock_change_at: string | null;
  /**
   * Livello di escalation raggiunto: 0 = nessuno. Monotono, non azzerato alla
   * chiusura; read-only lato Client (lo scrive solo il worker, sez. 14.1).
   */
  escalation_level: number;
  /**
   * Massimo escalation_levels della policy dell'esecuzione (1..5, fallback 1
   * quando il target non ha escalation). Serve a mostrare "livello n/m".
   */
  escalation_levels: number;
  as_of: string;
}

export interface SlaInstancesResponse {
  instances: SlaInstance[];
  pagination: SlaPaginationMeta;
}

/** Filtri accettati dal controller: 0/assente = tutti. */
export interface SlaInstancesQuery {
  level?: SlaTargetType | 0;
  health?: SlaHealth | -1;
  page?: number;
  pagesize?: number;
}

// ─── KPI (GET /sla/organizations/{org}/kpi) ──────────────────────────────────

export interface SlaKpiRow {
  on_time: number;
  at_risk: number;
  breached: number;
  total: number;
  as_of: string;
}

export interface SlaKpiResponse {
  kpi: SlaKpiRow;
}

// ─── Diagramma (GET /sla/organizations/{org}/models/{m}/diagram) ─────────────

/**
 * Un nodo per riga. I nodi senza SLA sono inclusi (`has_sla = false`): l'assenza
 * di SLA e' un dato neutro, non un verde implicito.
 */
export interface SlaDiagramNode {
  /** 0 = evento, 1 = attivita', 2 = gateway (come processtokens.flownodetype). */
  flownodetype: 0 | 1 | 2;
  idnode: number;
  designerid: string | null;
  name: string;
  idswimlane: number;
  has_sla: boolean;
  /**
   * Il Server dichiara se il nodo puo' ricevere uno SLA di attivita' (task
   * utente/manuale/servizio/script/robot; mai sottoprocessi, call activity,
   * eventi o gateway). Opzionale: un Server precedente non lo espone e la UI
   * ricade su `flownodetype = 1`.
   */
  sla_target_allowed?: boolean;
  /** Tipo di attivita' del nodo (NULL per eventi/gateway); informativo. */
  activity_type?: number | null;
  active: number;
  on_time: number;
  at_risk: number;
  breached: number;
  problematic: number;
  /** NULL se il nodo non ha istanze aperte. */
  worst_health: SlaHealth | null;
  /**
   * Istanze aperte sullo stesso designerid con revisione di origine diversa.
   * Le istanze su nodi rimossi dal modello corrente non compaiono affatto
   * (limite dichiarato, sez. 11.3): il recupero resta nella lista Monitor.
   */
  other_revision_instances: number;
  next_deadline_at: string | null;
  remaining_working_seconds: number | null;
  overdue_working_seconds: number | null;
  clock_running: boolean | null;
  next_clock_change_at: string | null;
  as_of: string;
}

export interface SlaDiagramResponse {
  diagram: SlaDiagramNode[];
  /** Serializzato come stringa dal controller. */
  meta: { model_revision: string };
}

// ─── Override e ricalcolo (admin-only) ───────────────────────────────────────

/**
 * PATCH /sla/organizations/{org}/instances/{id}.
 * La rotta implementata accetta solo la deadline assoluta: l'alternativa
 * "durata" prevista dalla tabella C1 non e' realizzata (punto aperto F1).
 */
export interface SlaOverrideRequest {
  expected_revision: number;
  /** Obbligatorio: senza motivo il Server risponde 422. */
  reason: string;
  /** ISO 8601; obbligatorio nella rotta implementata. */
  effective_deadline_at: string;
}

export interface SlaRecalculateRequest {
  expected_revision: number;
  reason: string;
}

/** Risposta comune delle mutazioni: 202 con Location e messaggio. */
export interface SlaMutationResponse {
  success: boolean;
  responsemessage: string;
}

// ─── Impostazioni di organizzazione (admin-only) ─────────────────────────────

/** GET: modello DMVC serializzato in minuscolo senza underscore. */
export interface SlaSettingsDto {
  idorganization: number;
  enabled: boolean;
  riskremainingpercent: number;
  nudgeremainingpercent: number;
  /** Gate di organizzazione: senza questo l'escalation per target resta inerte. */
  escalationenabled: boolean;
  digestenabled: boolean;
  digestat: string | null;
  mindurationminutes: number;
  maxnotificationlatencyseconds: number;
  updatedat: string | null;
  updatedby: number | null;
}

/** PUT: chiavi snake_case lette dal controller; i campi assenti non sono toccati. */
export interface SlaSettingsInput {
  enabled?: boolean;
  risk_remaining_percent?: number;
  nudge_remaining_percent?: number;
  escalation_enabled?: boolean;
  digest_enabled?: boolean;
  min_duration_minutes?: number;
  max_notification_latency_seconds?: number;
}

// ─── Preferenze utente (GET/PATCH /sla/preferences) ──────────────────────────

export interface SlaUserPreferencesDto {
  iduser: number;
  idorganization: number;
  digestenabled: boolean;
  escalationenabled: boolean;
  /**
   * Due interruttori separati (decisione 2, 2026-09-20): il riepilogo
   * (`digestenabled`) e le notifiche transazionali. Colonna Server
   * `sla_user_preferences.transactional_enabled boolean NOT NULL DEFAULT true`:
   * sempre presente nella risposta, quindi non opzionale come gli altri due.
   */
  transactionalenabled: boolean;
  greetingdismissedat: string | null;
  updatedat: string | null;
}

export interface SlaUserPreferencesInput {
  /** Il GET lo richiede come query string; il PATCH puo' portarlo nel body. */
  idorganization?: number;
  digest_enabled?: boolean;
  escalation_enabled?: boolean;
  /** Interruttore separato delle notifiche transazionali (decisione 2, 2026-09-20). */
  transactional_enabled?: boolean;
  /** Il saluto si chiude una volta sola: il Server registra solo la chiusura. */
  greeting_dismissed?: boolean;
}

// ─── Statistiche SLA (backend Python, contratto C1 sez. 5; P2) ───────────────

/**
 * Forma **letta dal contratto reale** del backend statistiche Python, non
 * dedotta: `backend_statistiche/app/schemas/sla.py:102-114`
 * (`SlaComplianceResponse`), `:69-99` (`SlaBasisCounts`,
 * `SlaExcludedBreakdown`, `SlaSample`), `:42-64` (`SlaStatisticsQuery`) e
 * `:159-187` (`controllers/sla.py`, rotta `GET /sla/{org}/compliance`).
 * I campi sono esattamente gli alias dello schema: nessuno inventato.
 *
 * Limiti dichiarati, non simulati:
 * - la rotta **non** e' montata in questo Client: `REACT_APP_STATS_BACKEND_BASE_URI`
 *   e' il backend Python P2 (piano Client sez. 5, `slaApi.ts`). F4a la consuma
 *   con la fixture locale quando `REACT_APP_SLA_FIXTURES=true`;
 * - trend e drill-down hanno schemi P2 propri (`SlaTrendResponse`,
 *   `SlaDrilldownResponse`) e **non** sono F4a: il loro DTO si e' aggiunto con F4b;
 * - **export (F4c): nessun DTO qui.** Il contratto P2 non ha una rotta di export
 *   (`controllers/sla.py:166-287`: compliance, trend e due drill-down) e le chiavi
 *   `sla.export.*` del catalogo P1 hanno `renderer: client`: il documento e' un
 *   artefatto del client costruito dai payload qui sopra
 *   (`model/slaStatisticsExportModel.ts`), quindi non c'e' una risposta da
 *   modellare;
 * - **insight (F4c): nessun DTO e nessuna rotta da consumare.** La rotta
 *   pianificata `GET /sla/{org}/insights`
 *   (`Flowenti-Server/plans/SLA_Implementation_Plan.md:280`) non esiste nel
 *   controller Python ne' in quello Delphi, e non esiste la tabella cache
 *   `ai_sla_suggestions`: senza sorgente il Client non modella una risposta che
 *   nessuno produce.
 */
export interface SlaComplianceBase {
  met: number;
  eligible: number;
  /** Zero denominatore -> null/N-D, non 0%. */
  percent: number | null;
}

/**
 * Chiusure eleggibili **non** conformi, fuori dal denominatore. Il terzo esito
 * (`authority_terminated`, `completion_kind = 4`) e' nel contratto P2
 * (`schemas/sla.py:85-88`, `services/sla/statistics.py:80-82`) e resta separato
 * dal denominatore come annullati e interrotti.
 */
export interface SlaComplianceExcluded {
  cancelled: number;
  interrupted: number;
  authority_terminated: number;
}

/** Periodo `[from, to)` nella timezone organizzativa, date ISO (giorni locali). */
export interface SlaCompliancePeriod {
  from: string;
  to: string;
}

/** Dimensione del campione: `limit`/`limited` rendono espliciti i dati limitati. */
export interface SlaComplianceSample {
  rows: number;
  limit: number | null;
  limited: boolean;
}

/**
 * Filtri condivisi da conformita', trend e drill-down (P2). `from`/`to` sono date
 * (periodo `[from, to)` a mezzanotte locale), `level` vale `process`/`task` ed e'
 * **obbligatorio** per conformita' e trend (`schemas/sla.py:26,45-47`). Le liste
 * viaggiano come stringhe separate da virgola (`controllers/sla.py:44-59`).
 *
 * `level` e' **opzionale** nel tipo perche' il drill-down non lo manda: li' il
 * livello e' nel **path** (`drilldown/process|step`, `controllers/sla.py:225-278`)
 * e la query del contratto non lo prevede. Chi costruisce una query per
 * conformita'/trend lo valorizza sempre (`slaStatisticsQuery`).
 */
export interface SlaComplianceQuery {
  from: string;
  to: string;
  level?: SlaComplianceLevel;
  modelids?: string;
  teamids?: string;
  customervalues?: string;
  granularity?: 'day' | 'week';
  page?: number;
  page_size?: number;
}

/** Livelli del contratto P2: processo o task, nessun aggregato dei due. */
export type SlaComplianceLevel = 'process' | 'task';

export interface SlaComplianceResponse {
  level: SlaComplianceLevel;
  period: SlaCompliancePeriod;
  original: SlaComplianceBase;
  effective: SlaComplianceBase;
  excluded: SlaComplianceExcluded;
  without_sla: number;
  /** Copertura = risultati con SLA / risultati totali; `null` (N/D) a totale zero. */
  coverage_percent: number | null;
  /** Tempo medio su base lavorativa dai secondi persistiti; `null` se non eleggibile. */
  average_working_seconds: number | null;
  sample: SlaComplianceSample;
  timezone: string;
  as_of: string;
  /**
   * Chiavi del catalogo P1 che la superficie renderizza (`services/sla/statistics.py:92-108`):
   * l'API **non manda prosa**, espone le chiavi. Il Client le usa quando presenti
   * e ricade sulle proprie costanti (stessi valori) quando mancano.
   */
  message_keys: Record<string, string>;
}

/**
 * **Trend** e **drill-down** (F4b, piano Client §5 F4b, §8 riga 128). Forma letta
 * dal contratto reale del backend statistiche (`backend_statistiche/app/schemas/sla.py`),
 * non dedotta:
 * - `:117-133` `SlaTrendPoint` / `SlaTrendResponse`;
 * - `:136-162` `SlaDrilldownRow`;
 * - `:165-181` `SlaPagination` / `SlaDrilldownResponse`;
 * - rotte `GET /sla/{org}/trend` e `GET /sla/{org}/drilldown/process|step`
 *   (`controllers/sla.py:190-278`).
 *
 * Proprieta' del contratto che la superficie **non** deve aggirare:
 * - **un solo conteggio per risultato**: `sla_result.idinstance` e' `UNIQUE`
 *   (`Flowenti-Server/TestServer/data/SCRIPT/20260916_sla_core.sql:221-222`) e le
 *   tre superfici condividono gli stessi predicati e gli stessi conteggi
 *   (`services/sla/queries.py:48-73` e `:124-152`): un breach escalato piu' volte
 *   produce **una** riga di risultato, quindi **un** breach. Il contratto non ha
 *   alcuna dimensione di escalation e il Client non ne aggiunge una;
 * - **nessuna metrica per persona**: `SlaDrilldownRow` non porta utente ne' ruolo
 *   (`services/sla/statistics.py:334-339`);
 * - **identita' storiche stabili**: l'identita' di un punto di trend e' il suo
 *   `bucket` (giorno o inizio settimana locale risolto in SQL, `queries.py:73-79`)
 *   e quella di una riga di dettaglio e' `sla_instance_id` (`sla_result.idinstance`);
 * - **nessun campo di modello/versione** nella riga: `sla_result` non ha una
 *   colonna di revisione, quindi `sla.statistics.drilldown.model_version` resta una
 *   chiave del catalogo P1 **senza dato** da rendere (dichiarata, non resa).
 */
export type SlaStatisticsGranularity = 'day' | 'week';

/** Un punto del trend: gli stessi conteggi della conformita', sul proprio bucket. */
export interface SlaTrendPoint {
  /** Identita' del punto: giorno o inizio settimana **locale**, ISO, dal Server. */
  bucket: string;
  original: SlaComplianceBase;
  effective: SlaComplianceBase;
  excluded: SlaComplianceExcluded;
  without_sla: number;
}

export interface SlaTrendResponse {
  level: SlaComplianceLevel;
  granularity: SlaStatisticsGranularity;
  period: SlaCompliancePeriod;
  timezone: string;
  as_of: string;
  /** `limit` = tetto dei bucket (400, `statistics.py:88`), `limited` = serie troncata. */
  sample: SlaComplianceSample;
  data: SlaTrendPoint[];
  message_keys: Record<string, string>;
}

/**
 * Una riga di dettaglio. `completion_kind` 1 = completamento valido (l'unico
 * eleggibile), 2/3/4 = annullato/interrotto/chiuso d'autorita'; `original_met` e
 * `effective_met` esistono **solo** per `completion_kind = 1` (vincolo DDL
 * `sla_result_met_only_when_completed_ck`).
 */
export interface SlaDrilldownRow {
  sla_instance_id: number;
  process_id: number;
  /** Livello del target: 1 processo, 2 attivita' (stessa scala del Monitor). */
  target_type: number;
  target_designer_id: string | null;
  /**
   * Nome leggibile del nodo (attivita'), quando il backend Python lo fornisce;
   * opzionale: in assenza la UI ricade su `target_designer_id`.
   */
  target_name?: string | null;
  target_node_id: number | null;
  closed_at: string;
  completion_kind: number;
  eligible: boolean;
  original_met: boolean | null;
  effective_met: boolean | null;
  original_deadline_at: string;
  effective_deadline_at: string;
  consumed_working_seconds: number;
  /**
   * Dimensione storica: valore della variabile `isStatisticsKey` risolto **alla
   * chiusura** (`20260916_sla_core.sql:237-239`), non ridotto dal `userschema`
   * corrente. E' la dimensione del filtro Cliente.
   */
  customer_value: string | null;
  team_id: number | null;
}

export interface SlaPagination {
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
}

export interface SlaDrilldownResponse {
  level: SlaComplianceLevel;
  period: SlaCompliancePeriod;
  timezone: string;
  as_of: string;
  /** `limit` = `page_size`, `limited` = esistono altre pagine. */
  sample: SlaComplianceSample;
  data: SlaDrilldownRow[];
  pagination: SlaPagination;
  message_keys: Record<string, string>;
}

/**
 * **Ripartizione per modello** (`GET /sla/{org}/breakdown/model`, backend
 * Python `schemas/sla.py` `SlaModelBreakdownRow`): stessi predicati e conteggi
 * della conformita', raggruppati per `processes.idprocessmodel`, quindi la somma
 * dei gruppi coincide con le schede. `process_model_id`/`name` nulli = processo
 * non piu' leggibile. `missed` = eleggibili non conformi sulla base effettiva.
 */
export interface SlaModelBreakdownRow {
  process_model_id: number | null;
  name: string | null;
  original: SlaComplianceBase;
  effective: SlaComplianceBase;
  missed: number;
  excluded: SlaComplianceExcluded;
  without_sla: number;
  average_working_seconds: number | null;
}

export interface SlaModelBreakdownResponse {
  level: SlaComplianceLevel;
  period: SlaCompliancePeriod;
  timezone: string;
  as_of: string;
  /** `limit` = tetto dei gruppi (500), `limited` = gruppi troncati. */
  sample: SlaComplianceSample;
  data: SlaModelBreakdownRow[];
  message_keys: Record<string, string>;
}

// ─── Errori stabili ──────────────────────────────────────────────────────────

/**
 * Codici del contratto C1 sez. 5 e delle costanti Server
 * (`Commons/Messages.Utils.pas`, prefisso `SLA_`). Il backend oggi restituisce
 * il messaggio tradotto, non il codice: `toSlaApiError` in `slaApi.ts` deriva il
 * codice dallo status HTTP e non prende decisioni sul testo.
 */
export const SLA_ERROR_CODES = {
  adminRequired: 'sla_admin_required',
  revisionConflict: 'sla_revision_conflict',
  calendarUnavailable: 'sla_calendar_unavailable',
  durationInvalid: 'sla_duration_invalid',
  deadlineInPast: 'sla_deadline_in_past',
  targetMismatch: 'sla_target_mismatch',
  definitionMissing: 'sla_definition_missing',
  definitionNotFound: 'sla_definition_not_found',
  instanceNotFound: 'sla_instance_not_found',
  modelNotFound: 'sla_model_not_found',
  reasonRequired: 'sla_reason_required',
  forbidden: 'sla_forbidden',
  estimateUnavailable: 'sla_estimate_unavailable',
  /** PUT definizioni (400): il `target_designerid` non esiste nel modello. */
  targetNodeNotFound: 'sla_target_node_not_found',
  /** PUT definizioni (400): il nodo non ammette SLA (sottoprocesso, evento, gateway...). */
  targetNodeTypeNotAllowed: 'sla_target_node_type_not_allowed',
  /** PUT definizioni (400): la deadline assoluta vale solo per il processo. */
  absoluteDeadlineProcessOnly: 'sla_absolute_deadline_process_only',
} as const;

export type SlaErrorCode = (typeof SLA_ERROR_CODES)[keyof typeof SLA_ERROR_CODES];
